import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from '../../../contexts/SnackbarContext/SnackbarProvider';
import { useEventContext } from '../../../contexts/EventContext';
import { TournamentFormatEnum, TournamentType } from '../../../enums/enums';
import { buildDoublesBracketsPath } from '../../../routes/routePaths';
import {
  generateBracketRequest,
  getTournamentTeamsRequest,
} from '../../../api/requests/tournaments';
import SportTypeModal from './SportTypeModal';
import {
  BodyContainer,
  ButtonContainer,
  Container,
  GenerateButton,
  ScrollContainer,
} from './DoublesPlayersDetails';
import styled from 'styled-components';
import { Typography } from 'antd';
import { COLORS, FONT_WEIGHTS, TEXT_SIZES } from '../../../utils/constants';
import { Collapse } from 'antd';
import { Team } from '../../../types/types';

const { Panel } = Collapse;

const TeamsPlayersDetails = () => {
  const navigate = useNavigate();
  const { tournamentId } = useParams();
  const { tournamentType, refreshEventData } = useEventContext();
  const snackbar = useSnackbar();
  const [tournamentDetails, setTournamentDetails] = useState<Team[]>([]);
  const [isBracketModalVisible, setIsBracketModalVisible] = useState(false);

  const fetchTournamentDetails = async () => {
    try {
      const response = await getTournamentTeamsRequest(Number(tournamentId));
      if (response) {
        setTournamentDetails(response);
      } else {
        setTournamentDetails([]);
      }
    } catch (err) {
      console.error(err || 'Failed to fetch tournament teams');
      snackbar.error(String(err) || 'Failed to fetch tournament teams');
    }
  };

  const handleGenerateBracket = async (
    selectedFormat: TournamentFormatEnum,
  ) => {
    try {
      const response = await generateBracketRequest(
        Number(tournamentId),
        selectedFormat,
      );
      if (response && response.isSuccess) {
        snackbar.success('Bracket generated successfully!');
        setIsBracketModalVisible(false);
        // Navigate to brackets view after successful generation
        navigate('../doubles-brackets', { relative: 'path' });
        refreshEventData();
      } else {
        snackbar.error(response?.error);
      }
    } catch (error) {
      console.error('Error generating bracket:', error);
      snackbar.error('Failed to generate bracket. Please try again.');
    }
  };

  const parsedTournamentType = tournamentType
    ? parseInt(tournamentType)
    : TournamentType.Singles;

  useEffect(() => {
    fetchTournamentDetails();
  }, []);

  return (
    <Container>
      <BodyContainer>
        <Title>All Teams</Title>
        <ScrollContainer>
          <TeamsGrid>
            {tournamentDetails.map((team: Team) => (
              <StyledCollapse
                key={team.teamId}
                expandIconPosition="end"
                bordered={false}
              >
                <Panel header={team.teamName} key={team.teamId}>
                  <PlayersGrid>
                    {team.members.map((player, index) => (
                      <PlayerRow key={player.memberId}>
                        <Index>{index + 1}</Index>
                        <Name>{player.memberName}</Name>
                      </PlayerRow>
                    ))}
                  </PlayersGrid>
                </Panel>
              </StyledCollapse>
            ))}
          </TeamsGrid>
        </ScrollContainer>
      </BodyContainer>
      <ButtonContainer>
        <GenerateButton
          type="primary"
          onClick={() => setIsBracketModalVisible(true)}
          height="44px"
        >
          Generate brackets
        </GenerateButton>
      </ButtonContainer>

      {isBracketModalVisible && (
        <SportTypeModal
          visible={isBracketModalVisible}
          onProceed={handleGenerateBracket}
          closeModal={() => setIsBracketModalVisible(false)}
          tournamentType={parsedTournamentType}
        />
      )}
    </Container>
  );
};

export default TeamsPlayersDetails;

const Title = styled(Typography.Text)`
  display: block;
  text-align: center;
  margin-bottom: 24px;
  font-size: ${TEXT_SIZES.MD}px;
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
`;

const TeamsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const StyledCollapse = styled(Collapse)`
  background: transparent;
  border: none;

  .ant-collapse-item {
    border: 1px solid ${COLORS.skyBlue.primary};
    border-radius: 12px;
    overflow: hidden;
  }

  .ant-collapse-item:last-child {
    border-bottom: 1px solid ${COLORS.skyBlue.primary};
    border-radius: 12px;
  }

  .ant-collapse-header {
    padding: 16px 20px !important;
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    font-size: ${TEXT_SIZES.MD};
    background: ${COLORS.white.primary};
    align-items: center;
  }

  .ant-collapse-content {
    border-top: 1px solid ${COLORS.skyBlue.light};
    background: ${COLORS.white.primary};
  }

  .ant-collapse-content-box {
    padding: 16px;
  }
`;

const PlayersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
`;

const PlayerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: ${COLORS.skyBlue.light};
  border-radius: 8px;
`;

const Index = styled.span`
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
`;

const Name = styled.span`
  font-size: ${TEXT_SIZES.SM}px;
`;
